import { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'next-i18next'

import { useAppSelector, useAppDispatch } from '~/redux/hooks'
import { Breakpoint, useCheckBreakpoint } from '~/utils'

import GlobeAi from '~/aioracle/GlobeAi'
import oracleAnalytics from '~/analytics/oracle/oracleAnalytics'
import { OracleButtonClickTypes } from '~/analytics/oracle/oracleEvents.types'
import { selectHideHorizontalMenu } from '~/redux/slices/menu'
import { selectBootcampIsCollapsed } from '~/bootcamp/slices/bootcamp-page.slice'
import {
  selectIsChatAiOpen,
  selectShowFollowUpMessage,
  selectWasAutoTriggered,
  setChatAiIsOpenActionCreator,
  setShowFollowUpMessageActionCreator,
  setWasAutoTriggeredActionCreator,
} from '~/redux/slices/chat-ai'
import { setIsOpenActionCreator } from '~/redux/slices/chat'
import WelcomeMessage from '~/aioracle/WelcomeMessage'
import OracleFollowUpMessage from '~/aioracle/OracleFollowUpMessage'
import useShowWelcomeMessage from '~/aioracle/hooks/useShowWelcomeMessage'
import { getDesktopOracleLayout, getOracleOpenTarget } from '~/aioracle/utils/oraclePanelLayout.utils'
import { selectIsActivitySurface } from '~/aioracle/slices/learning-context.slice'

import stylesAi from './ChatAi.module.css'

export default function ChatAiOpenButton({ isMobile = false }: { isMobile: boolean }): React.ReactElement | null {
  const { t } = useTranslation('chatAi')
  const [showInitialAnimation, setShowInitialAnimation] = useState(false)
  const [showCloseAnimation, setShowCloseAnimation] = useState(false)
  const [openTarget, setOpenTarget] = useState<{ x: number; y: number } | null>(null)
  const dispatch = useAppDispatch()
  const hideBottomMenu = useAppSelector(selectHideHorizontalMenu)
  const isCollapsed = useAppSelector(selectBootcampIsCollapsed)
  const hideOracleButton = !isCollapsed && hideBottomMenu
  const isDesktop = useCheckBreakpoint({ gt: Breakpoint.MD })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const { showMessage, dismiss } = useShowWelcomeMessage()

  const isModalOpen = useAppSelector(selectIsChatAiOpen)
  const besideActivity = useAppSelector(selectIsActivitySurface)
  const wasAutoTriggered = useAppSelector(selectWasAutoTriggered)
  const showFollowUpMessage = useAppSelector(selectShowFollowUpMessage)

  // useEffect: when the chat is open, it will close the chat
  // it will basically check if the chat or initial modal is open or not
  // if the chat or initial modal is open, it will reset the initial animation and start closing animation
  // showInitialAnimation removed from the dependency array intentionally to make the animation work
  useEffect(() => {
    if (!isModalOpen && showInitialAnimation) {
      setShowInitialAnimation(false)
      setShowCloseAnimation(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isModalOpen])

  useEffect(() => {
    if (!isModalOpen && wasAutoTriggered) {
      dispatch(setWasAutoTriggeredActionCreator(false))
      dispatch(setShowFollowUpMessageActionCreator(true))
    }
  }, [isModalOpen, wasAutoTriggered, dispatch])

  const dismissFollowUp = (): void => {
    dispatch(setShowFollowUpMessageActionCreator(false))
  }

  // handleOpenInitialAnimation function: when the chat is minimized, it will check if the chat is open or not
  // if the chat is open, it will close the chat
  const handleOpenInitialAnimation = (): void => {
    dispatch(setIsOpenActionCreator(false)) // close the chat
    if (showMessage) {
      dismiss() // dismiss the welcome message if it is showing
    }
    if (showFollowUpMessage) {
      dismissFollowUp()
    }
    if (!isModalOpen) {
      oracleAnalytics.logButtonClick(OracleButtonClickTypes.ORACLE_ICON)

      if (!isMobile) {
        // The button flies to where the panel will actually open, which is beside the activity card
        // rather than the middle of the screen while an activity set is up.
        const layout = getDesktopOracleLayout({
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
          besideActivity,
        })
        setOpenTarget(getOracleOpenTarget(layout))
        setShowInitialAnimation(!showInitialAnimation)
      }
    } else {
      dispatch(setChatAiIsOpenActionCreator(false))
    }
  }

  // onAnimationEndHandler function: when the animation ends, it will check if the chat is open or not
  // if it is open, it will close the chat, if it is closed, it will open the chat
  // if the user is not authenticated, it will redirect to the login page
  // if the user is authenticated, it will open the chat
  // if the user is authenticated and the chat ai consent is not done, it will open the chat ai initial modal
  // if the user is authenticated and the chat ai consent is done, it will open the chat
  const onAnimationEndHandler = (): void => {
    if (showCloseAnimation) {
      setShowCloseAnimation(false)
    } else {
      dispatch(setChatAiIsOpenActionCreator(true))
    }
  }

  const getButtonClassName = (): string => {
    if (showInitialAnimation) {
      return stylesAi['chat_ai__open-btn-initial-animation']
    } else if (showCloseAnimation) {
      return stylesAi['chat_ai__open-btn-closing-animation']
    } else if (hideOracleButton) {
      return stylesAi['chat_ai__open-btn-hide']
    } else {
      return stylesAi['chat_ai__open-btn-initial-position']
    }
  }

  if (!(isModalOpen && isDesktop)) {
    return (
      <>
        {showMessage && <WelcomeMessage onClick={dismiss} />}
        {showFollowUpMessage && isMobile && <OracleFollowUpMessage onDismiss={dismissFollowUp} />}
        <button
          className={`rounded-4 whitespace-nowrap p-0 z-oracle text-md font-medium transform origin-bottom active:scale-95 delay-150 transition-all duration-300 hover:scale-105 hover:delay-0 ${
            isMobile ? 'absolute top-1 left-0 right-0 mx-auto' : 'fixed'
          } ${getButtonClassName()}`}
          data-testid="chat-open-btn"
          onAnimationEnd={onAnimationEndHandler}
          onClick={handleOpenInitialAnimation}
          ref={buttonRef}
          style={
            openTarget
              ? {
                  ['--oracle-open-target-x' as any]: `${openTarget.x}px`,
                  ['--oracle-open-target-y' as any]: `${openTarget.y}px`,
                }
              : undefined
          }
        >
          <div className="relative flex flex-col w-18 md:w-30 h-18 md:h-32 -ml-0.5 mx-auto mb-0.5 items-center text-white cursor-pointer">
            <GlobeAi />
            {showFollowUpMessage && !isMobile && (
              <span className="text-white text-xs font-medium text-center leading-4 mt-0.5 whitespace-nowrap">
                {t('alwaysHereForYou')}
              </span>
            )}
          </div>
        </button>
      </>
    )
  } else {
    return null
  }
}
